import { useClient } from '@cortex/react-query';
import type { Evaluation, Suite, Task, WorkflowListItem } from '@cortex/types';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, createFileRoute, useNavigate } from '@tanstack/react-router';
import { useState } from 'react';

import { BackButton } from '../../components/common/BackButton';
import { GraderTypeBadges } from '../../components/common/GraderTypeBadges';
import { PageHeader } from '../../components/common/PageHeader';
import { SkeletonKpiCards, SkeletonTable } from '../../components/common/Skeleton';
import { GateVerdictBanner } from '../../components/evals/GateVerdictBanner';
import { GraderCoverageMatrix } from '../../components/suites/GraderCoverageMatrix';
import { SuiteTrendChart } from '../../components/suites/SuiteTrendChart';
import { routes } from '../../lib/constants';
import { formatDateTime } from '../../lib/format';

export const Route = createFileRoute('/suites/$suiteId')({ component: SuiteDetailPage });

interface IListEnvelope<T> {
  items: T[];
  total: number;
}

function extractPassRate(e: Evaluation): number | null {
  const summary = (e.scoreSummary as Record<string, unknown> | null) ?? null;
  if (!summary) return null;
  const taskResults = summary.taskResults;
  if (Array.isArray(taskResults) && taskResults.length > 0) {
    const passed = (taskResults as Array<{ passedTrials: number; nTrials: number }>).filter(
      (tr) => tr.passedTrials === tr.nTrials,
    ).length;
    return passed / taskResults.length;
  }
  const total = typeof summary.total === 'number' ? (summary.total as number) : 0;
  const passed = typeof summary.passed === 'number' ? (summary.passed as number) : 0;
  return total > 0 ? passed / total : null;
}

// ─── Meta Card ──────────────────────────────────────────────────────────────

function MetaCard({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div
      style={{
        background: 'var(--surface-1)',
        border: '1px solid var(--border-subtle)',
        borderRadius: 'var(--radius-md)',
        padding: '14px 16px',
      }}
    >
      <div
        style={{
          fontFamily: 'var(--font-mono)',
          fontSize: 9,
          letterSpacing: '0.18em',
          color: 'var(--text-muted)',
          textTransform: 'uppercase',
        }}
      >
        {label}
      </div>
      <div style={{ fontSize: 18, fontWeight: 600, marginTop: 4 }}>{children}</div>
    </div>
  );
}

// ─── Edit Modal ─────────────────────────────────────────────────────────────

function EditModal({
  suite,
  workflows,
  onClose,
  onSaved,
}: {
  suite: Suite;
  workflows: WorkflowListItem[];
  onClose: () => void;
  onSaved: () => void;
}) {
  const client = useClient();

  const [name, setName] = useState(suite.name);
  const [kind, setKind] = useState(suite.kind);
  const [gatesRun, setGatesRun] = useState(suite.gatesRun);
  const [description, setDescription] = useState(suite.description ?? '');
  const [editWorkflowId, setEditWorkflowId] = useState(
    ((suite as unknown as Record<string, unknown>).workflowId as string) ?? '',
  );
  const [expectedPassRate, setExpectedPassRate] = useState(
    suite.expectedPassRate != null ? String(suite.expectedPassRate) : '',
  );

  const update = useMutation({
    mutationFn: () =>
      client.http
        .patch(`v1/suites/${suite.id}`, {
          json: {
            name: name.trim(),
            description: description.trim() || null,
            workflowId: editWorkflowId || null,
            kind,
            gatesRun,
            expectedPassRate: expectedPassRate ? Number(expectedPassRate) : null,
          },
        })
        .json<Suite>(),
    onSuccess: () => {
      onSaved();
      onClose();
    },
  });

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 9000,
      }}
      onKeyDown={(e) => e.key === 'Escape' && onClose()}
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        className="card"
        style={{ width: 480, maxHeight: '80vh', overflow: 'auto' }}
        onClick={(e) => e.stopPropagation()}
      >
        <div
          className="card-body"
          style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-3)' }}
        >
          <h3 style={{ margin: 0, fontSize: 16 }}>Edit Suite</h3>

          <label className="field">
            <span className="field-label">Name</span>
            <input
              type="text"
              className="input"
              value={name}
              onChange={(e) => setName(e.target.value)}
              maxLength={200}
            />
          </label>

          <label className="field">
            <span className="field-label">Description</span>
            <textarea
              className="textarea"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="What this suite measures"
              maxLength={2000}
              rows={3}
            />
          </label>

          <label className="field">
            <span className="field-label">Workflow</span>
            <select
              className="input"
              value={editWorkflowId}
              onChange={(e) => setEditWorkflowId(e.target.value)}
            >
              <option value="">Not specified</option>
              {workflows.map((w) => (
                <option key={w.id} value={w.id}>
                  {w.name}
                </option>
              ))}
            </select>
          </label>

          <div className="field">
            <span className="field-label">Kind</span>
            <div style={{ display: 'flex', gap: 'var(--sp-2)' }}>
              {(['CAPABILITY', 'REGRESSION'] as const).map((k) => (
                <label
                  key={k}
                  style={{
                    flex: 1,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                    padding: '8px 12px',
                    border: `1px solid ${kind === k ? 'var(--accent-primary)' : 'var(--border-subtle)'}`,
                    borderRadius: 'var(--radius-sm)',
                    cursor: 'pointer',
                  }}
                >
                  <input
                    type="radio"
                    name="edit-kind"
                    value={k}
                    checked={kind === k}
                    onChange={() => setKind(k)}
                    style={{ margin: 0 }}
                  />
                  <span style={{ fontSize: 13 }}>{k}</span>
                </label>
              ))}
            </div>
          </div>

          {kind === 'REGRESSION' ? (
            <>
              <label
                className="field"
                style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}
              >
                <input
                  type="checkbox"
                  checked={gatesRun}
                  onChange={(e) => setGatesRun(e.target.checked)}
                />
                <span className="field-label" style={{ margin: 0 }}>
                  Gates Run
                </span>
              </label>
              <label className="field">
                <span className="field-label">Expected Pass Rate</span>
                <input
                  type="number"
                  className="input"
                  min={0}
                  max={1}
                  step={0.01}
                  value={expectedPassRate}
                  onChange={(e) => setExpectedPassRate(e.target.value)}
                  placeholder="0.95"
                  style={{ maxWidth: 120 }}
                />
              </label>
            </>
          ) : null}

          <div style={{ display: 'flex', gap: 'var(--sp-2)', justifyContent: 'flex-end' }}>
            <button type="button" className="btn btn--ghost btn--sm" onClick={onClose}>
              Cancel
            </button>
            <button
              type="button"
              className="btn btn--primary btn--sm"
              disabled={!name.trim() || update.isPending}
              onClick={() => update.mutate()}
            >
              {update.isPending ? 'Saving...' : 'Save'}
            </button>
          </div>
          {update.error ? (
            <span className="t-xs" style={{ color: 'var(--status-danger)' }}>
              {(update.error as Error).message}
            </span>
          ) : null}
        </div>
      </div>
    </div>
  );
}

// ─── Main Page ──────────────────────────────────────────────────────────────

function SuiteDetailPage() {
  const { suiteId } = Route.useParams();
  const client = useClient();
  const qc = useQueryClient();
  const nav = useNavigate();

  const [showEdit, setShowEdit] = useState(false);
  const [taskSearch, setTaskSearch] = useState('');
  const [taskPickerOpen, setTaskPickerOpen] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);

  // ── Queries ──
  const suite = useQuery({
    queryKey: ['suite', suiteId],
    queryFn: () => client.http.get(`v1/suites/${suiteId}`).json<Suite>(),
  });

  const workflows = useQuery({
    queryKey: ['workflows', 'for-suite-detail'],
    queryFn: () =>
      client.http.get('v1/workflows?limit=200').json<IListEnvelope<WorkflowListItem>>(),
  });

  const allTasks = useQuery({
    queryKey: ['tasks', 'all-for-suite-detail'],
    queryFn: () => client.http.get('v1/tasks?limit=200').json<IListEnvelope<Task>>(),
  });

  const recentEvals = useQuery({
    queryKey: ['evaluations', 'for-suite', suiteId],
    queryFn: () =>
      client.http
        .get(`v1/evaluations?suiteId=${encodeURIComponent(suiteId)}&limit=10`)
        .json<IListEnvelope<Evaluation>>(),
  });

  // ── Mutations ──
  const addTask = useMutation({
    mutationFn: (taskId: string) => client.http.put(`v1/suites/${suiteId}/tasks/${taskId}`).text(),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ['suite', suiteId] });
      setTaskSearch('');
      setTaskPickerOpen(false);
    },
  });

  const removeTask = useMutation({
    mutationFn: (taskId: string) =>
      client.http.delete(`v1/suites/${suiteId}/tasks/${taskId}`).text(),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ['suite', suiteId] });
    },
  });

  const deleteSuite = useMutation({
    mutationFn: () => client.http.delete(`v1/suites/${suiteId}`).text(),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ['suites'] });
      await nav({ to: routes.SUITES });
    },
  });

  // ── Loading / Error ──
  if (suite.isLoading) {
    return (
      <div style={{ padding: '24px 36px' }}>
        <SkeletonKpiCards count={4} />
        <SkeletonTable rows={3} cols={4} />
      </div>
    );
  }
  if (suite.error) return <div className="alert alert--danger">{String(suite.error)}</div>;
  if (!suite.data) return null;

  const s = suite.data;
  const memberTaskIds = new Set(s.tasks.map((t) => t.taskId));
  const taskList = allTasks.data?.items ?? [];
  const memberTasks = taskList.filter((t) => memberTaskIds.has(t.id));
  const availableTasks = taskList.filter((t) => !memberTaskIds.has(t.id));
  const filteredTasks = taskSearch
    ? availableTasks.filter(
        (t) =>
          t.name.toLowerCase().includes(taskSearch.toLowerCase()) ||
          (t.description ?? '').toLowerCase().includes(taskSearch.toLowerCase()),
      )
    : availableTasks;

  const suiteEvals = recentEvals.data?.items ?? [];

  // Gate verdict from latest completed eval
  const latestCompleted = suiteEvals.find((e) => e.status === 'COMPLETED');
  const latestPassRate = latestCompleted ? extractPassRate(latestCompleted) : null;
  const gateStatus =
    s.gatesRun && latestCompleted
      ? latestPassRate != null && s.expectedPassRate != null && latestPassRate >= s.expectedPassRate
        ? 'passed'
        : 'failed'
      : null;

  return (
    <>
      <PageHeader
        title={s.name}
        actions={
          <div style={{ display: 'flex', gap: 8 }}>
            <BackButton to={routes.SUITES} label="Suites" />
            <button className="btn btn--primary btn--sm" onClick={() => setShowEdit(true)}>
              Edit
            </button>
            <button
              className="btn btn--sm"
              style={{ color: 'var(--status-danger)', borderColor: 'var(--status-danger)' }}
              onClick={() => setConfirmDelete(true)}
            >
              Delete
            </button>
          </div>
        }
      />
      <div style={{ paddingBottom: 60 }}>
        {/* Meta cards
         * Note: the spec calls for an "entity reference" card (which workflow
         * this suite evaluates). Currently the Suite schema has no workflowId —
         * suites are implicitly scoped by convention. Adding explicit entity
         * scoping requires a schema migration + UI for selecting the workflow.
         */}
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))',
            gap: 16,
            marginBottom: 24,
          }}
        >
          <MetaCard label="Workflow">
            <span style={{ fontSize: 13 }}>
              {(() => {
                const wfId = (s as unknown as Record<string, unknown>).workflowId as string | null;
                if (!wfId) return <span className="t-muted">Not specified</span>;
                const wf = (workflows.data?.items ?? []).find((w) => w.id === wfId);
                return wf?.name ?? wfId.slice(0, 12);
              })()}
            </span>
          </MetaCard>
          <MetaCard label="Kind">
            <span
              className="badge"
              style={{
                background:
                  s.kind === 'REGRESSION' ? 'var(--status-danger-bg)' : 'var(--status-info-bg)',
                color: s.kind === 'REGRESSION' ? 'var(--status-danger)' : 'var(--status-info)',
                border: `1px solid ${s.kind === 'REGRESSION' ? 'var(--status-danger-bd)' : 'var(--status-info-bd)'}`,
                fontSize: 11,
              }}
            >
              {s.kind}
            </span>
          </MetaCard>
          <MetaCard label="Tasks">{s.tasks.length}</MetaCard>
          <MetaCard label="Gates Run">
            <span style={{ color: s.gatesRun ? 'var(--status-success)' : 'var(--text-muted)' }}>
              {s.gatesRun ? '✓ Yes' : '✗ No'}
            </span>
          </MetaCard>
          <MetaCard label="Expected Pass Rate">
            {s.expectedPassRate != null ? `${(s.expectedPassRate * 100).toFixed(0)}%` : '—'}
          </MetaCard>
        </div>

        {/* Gate verdict banner */}
        {gateStatus ? (
          <GateVerdictBanner
            status={gateStatus}
            detail={
              latestCompleted
                ? `Last run: ${latestCompleted.id.slice(0, 12)} · pass rate ${latestPassRate != null ? `${(latestPassRate * 100).toFixed(1)}%` : '—'}${s.expectedPassRate != null ? ` · threshold ${(s.expectedPassRate * 100).toFixed(0)}%` : ''}`
                : ''
            }
          />
        ) : null}

        {/* Pass rate trend chart */}
        {(() => {
          const wfId = (s as unknown as Record<string, unknown>).workflowId as string | null;
          return wfId ? (
            <SuiteTrendChart
              workflowId={wfId}
              suiteId={suiteId}
              expectedPassRate={s.expectedPassRate}
            />
          ) : null;
        })()}

        {/* Task membership */}
        <div style={{ marginBottom: 28 }}>
          <div
            style={{
              fontFamily: 'var(--font-mono)',
              fontSize: 9,
              fontWeight: 600,
              textTransform: 'uppercase',
              letterSpacing: '0.18em',
              color: 'var(--text-muted)',
              marginBottom: 12,
              paddingBottom: 8,
              borderBottom: '1px solid var(--border-subtle)',
            }}
          >
            Tasks ({s.tasks.length})
          </div>

          {/* Add task picker */}
          <div
            style={{ position: 'relative', marginBottom: 12 }}
            onBlur={(e) => {
              if (!e.currentTarget.contains(e.relatedTarget as Node)) {
                setTimeout(() => setTaskPickerOpen(false), 150);
              }
            }}
          >
            <input
              type="text"
              className="input"
              style={{ maxWidth: 400 }}
              placeholder={
                allTasks.isLoading
                  ? 'Loading tasks...'
                  : availableTasks.length === 0
                    ? 'All tasks are in this suite'
                    : '+ Add task to suite...'
              }
              value={taskSearch}
              onChange={(e) => {
                setTaskSearch(e.target.value);
                setTaskPickerOpen(true);
              }}
              onFocus={() => setTaskPickerOpen(true)}
              disabled={allTasks.isLoading || availableTasks.length === 0}
            />
            {taskPickerOpen && filteredTasks.length > 0 ? (
              <div
                style={{
                  position: 'absolute',
                  top: '100%',
                  left: 0,
                  width: 400,
                  border: '1px solid var(--border-subtle)',
                  borderRadius: 'var(--radius-sm)',
                  maxHeight: 200,
                  overflowY: 'auto',
                  background: 'var(--surface-1)',
                  zIndex: 100,
                }}
              >
                {filteredTasks.map((t) => (
                  <button
                    key={t.id}
                    type="button"
                    onClick={() => addTask.mutate(t.id)}
                    style={{
                      display: 'block',
                      width: '100%',
                      textAlign: 'left',
                      padding: '8px 12px',
                      border: 'none',
                      background: 'none',
                      color: 'var(--text-primary)',
                      cursor: 'pointer',
                      fontSize: 13,
                      borderBottom: '1px solid var(--border-subtle)',
                    }}
                    onMouseEnter={(e) => {
                      (e.target as HTMLElement).style.background = 'var(--surface-2)';
                    }}
                    onMouseLeave={(e) => {
                      (e.target as HTMLElement).style.background = 'none';
                    }}
                  >
                    {t.name}
                    {t.description ? (
                      <span className="t-muted" style={{ marginLeft: 8, fontSize: 11 }}>
                        {t.description.slice(0, 60)}
                      </span>
                    ) : null}
                  </button>
                ))}
              </div>
            ) : null}
          </div>

          {/* Task membership table */}
          {memberTasks.length > 0 ? (
            <div className="card" style={{ overflow: 'hidden' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                <thead>
                  <tr
                    style={{
                      borderBottom: '1px solid var(--border-subtle)',
                      textAlign: 'left',
                    }}
                  >
                    <th style={{ padding: '10px 16px', fontWeight: 500, fontSize: 12 }}>Task</th>
                    <th style={{ padding: '10px 16px', fontWeight: 500, fontSize: 12 }}>Graders</th>
                    <th style={{ padding: '10px 16px', fontWeight: 500, fontSize: 12 }}>Scoring</th>
                    <th
                      style={{ padding: '10px 16px', fontWeight: 500, fontSize: 12, width: 40 }}
                    />
                  </tr>
                </thead>
                <tbody>
                  {memberTasks.map((t) => (
                    <tr
                      key={t.id}
                      style={{ borderBottom: '1px solid var(--border-subtle)', cursor: 'pointer' }}
                      onClick={() => nav({ to: routes.taskDetail(t.id) })}
                    >
                      <td style={{ padding: '10px 16px' }}>
                        <Link
                          to={routes.taskDetail(t.id)}
                          style={{ fontWeight: 500, color: 'var(--text-primary)' }}
                          onClick={(e) => e.stopPropagation()}
                        >
                          {t.name}
                        </Link>
                        {t.description ? (
                          <div className="t-xs t-muted" style={{ marginTop: 2 }}>
                            {t.description.slice(0, 80)}
                          </div>
                        ) : null}
                      </td>
                      <td style={{ padding: '10px 16px' }}>
                        <GraderTypeBadges graders={t.graders} />
                      </td>
                      <td style={{ padding: '10px 16px' }}>
                        {t.scoring ? (
                          <span className="t-xs" style={{ fontFamily: 'var(--font-mono)' }}>
                            {t.scoring.mode}
                            {t.scoring.threshold != null ? ` · ${t.scoring.threshold}` : ''}
                          </span>
                        ) : (
                          <span className="t-muted">—</span>
                        )}
                      </td>
                      <td style={{ padding: '10px 16px', textAlign: 'center' }}>
                        <button
                          className="btn btn--ghost btn--sm"
                          title="Remove from suite"
                          onClick={(e) => {
                            e.stopPropagation();
                            removeTask.mutate(t.id);
                          }}
                          style={{ fontSize: 16, padding: '2px 6px', lineHeight: 1 }}
                        >
                          x
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="card" style={{ padding: '32px 24px', textAlign: 'center' }}>
              <div className="t-muted" style={{ marginBottom: 8 }}>
                No tasks in this suite yet.
              </div>
              <span className="t-xs t-muted">
                Use the search above to add tasks, or{' '}
                <Link to={routes.TASKS_NEW} style={{ color: 'var(--accent-primary)' }}>
                  create a new task
                </Link>
                .
              </span>
            </div>
          )}
        </div>

        {/* Grader coverage matrix */}
        {memberTasks.length > 1 ? <GraderCoverageMatrix tasks={memberTasks} /> : null}

        {/* Recent eval runs */}
        <div style={{ marginBottom: 28 }}>
          <div
            style={{
              fontFamily: 'var(--font-mono)',
              fontSize: 9,
              fontWeight: 600,
              textTransform: 'uppercase',
              letterSpacing: '0.18em',
              color: 'var(--text-muted)',
              marginBottom: 12,
              paddingBottom: 8,
              borderBottom: '1px solid var(--border-subtle)',
            }}
          >
            Recent Eval Runs
          </div>
          {suiteEvals.length > 0 ? (
            <div className="card" style={{ overflow: 'hidden' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                <thead>
                  <tr
                    style={{
                      borderBottom: '1px solid var(--border-subtle)',
                      textAlign: 'left',
                    }}
                  >
                    <th style={{ padding: '10px 16px', fontWeight: 500, fontSize: 12 }}>Run</th>
                    <th style={{ padding: '10px 16px', fontWeight: 500, fontSize: 12 }}>Status</th>
                    <th style={{ padding: '10px 16px', fontWeight: 500, fontSize: 12 }}>
                      Pass Rate
                    </th>
                    <th style={{ padding: '10px 16px', fontWeight: 500, fontSize: 12 }}>Started</th>
                  </tr>
                </thead>
                <tbody>
                  {suiteEvals.map((e) => (
                    <tr
                      key={e.id}
                      style={{ borderBottom: '1px solid var(--border-subtle)', cursor: 'pointer' }}
                      onClick={() => nav({ to: routes.evalDetail(e.id) })}
                    >
                      <td
                        style={{
                          padding: '10px 16px',
                          fontFamily: 'var(--font-mono)',
                          fontSize: 12,
                        }}
                      >
                        <Link
                          to={routes.evalDetail(e.id)}
                          style={{ color: 'var(--text-primary)' }}
                          onClick={(ev) => ev.stopPropagation()}
                        >
                          {e.id.slice(0, 12)}
                        </Link>
                      </td>
                      <td style={{ padding: '10px 16px' }}>
                        <span
                          className="badge"
                          style={{
                            fontSize: 10,
                            padding: '2px 6px',
                            background:
                              e.status === 'COMPLETED'
                                ? 'var(--status-success-bg, rgba(46,184,114,0.08))'
                                : e.status === 'FAILED'
                                  ? 'var(--status-danger-bg)'
                                  : e.status === 'RUNNING'
                                    ? 'var(--status-info-bg)'
                                    : 'var(--surface-2)',
                            color:
                              e.status === 'COMPLETED'
                                ? 'var(--status-success)'
                                : e.status === 'FAILED'
                                  ? 'var(--status-danger)'
                                  : e.status === 'RUNNING'
                                    ? 'var(--status-info)'
                                    : 'var(--text-muted)',
                          }}
                        >
                          {e.status}
                        </span>
                      </td>
                      <td
                        style={{
                          padding: '10px 16px',
                          fontFamily: 'var(--font-mono)',
                          fontSize: 12,
                        }}
                      >
                        {(() => {
                          const pr = extractPassRate(e);
                          return pr != null ? `${(pr * 100).toFixed(1)}%` : '—';
                        })()}
                      </td>
                      <td style={{ padding: '10px 16px' }}>
                        <span className="t-xs t-muted">
                          {e.startedAt ? formatDateTime(e.startedAt) : '—'}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="card" style={{ padding: '24px', textAlign: 'center' }}>
              <span className="t-muted">No eval runs for this suite yet.</span>
            </div>
          )}
        </div>

        {/* Delete confirmation */}
        {confirmDelete ? (
          <div
            className="card"
            style={{
              padding: '16px 20px',
              border: '1px solid var(--status-danger)',
              display: 'flex',
              alignItems: 'center',
              gap: 12,
            }}
          >
            <span>
              Delete suite <b>{s.name}</b>? This cannot be undone.
            </span>
            <button
              className="btn btn--sm"
              style={{ color: 'var(--status-danger)', borderColor: 'var(--status-danger)' }}
              onClick={() => deleteSuite.mutate()}
              disabled={deleteSuite.isPending}
            >
              {deleteSuite.isPending ? 'Deleting...' : 'Confirm delete'}
            </button>
            <button className="btn btn--ghost btn--sm" onClick={() => setConfirmDelete(false)}>
              Cancel
            </button>
          </div>
        ) : null}
      </div>

      {/* Edit modal */}
      {showEdit ? (
        <EditModal
          suite={s}
          workflows={workflows.data?.items ?? []}
          onClose={() => setShowEdit(false)}
          onSaved={() => qc.invalidateQueries({ queryKey: ['suite', suiteId] })}
        />
      ) : null}
    </>
  );
}
